import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Loader2, Lock } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CodeBlock, ScoreBar } from "@/components/Primitives";

const SEVERITY_COLOR = {
  SEV1: "text-destructive",
  SEV2: "text-[hsl(var(--warning))]",
  SEV3: "text-muted-foreground",
};

export function Labs() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/troubleshooting/scenarios").then(({ data }) => setData(data)).catch((e) => setError(errMsg(e, "Unable to load labs")));
  }, []);

  if (error) return <p data-testid="labs-error" className="text-sm text-destructive">{error}</p>;
  if (!data) return <div className="grid place-items-center py-24" data-testid="labs-loading"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-8 fade-up" data-testid="labs-page">
      <div>
        <p className="overline text-primary">troubleshooting labs</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-display font-black tracking-tighter">Production incidents, scored on reasoning</h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
          Five simulated incidents with real evidence. You are graded on diagnosis, mitigation order and prevention —
          not on guessing a keyword. Labs contribute 15% of your readiness score.
        </p>
        <p className="mt-3 font-mono text-xs text-muted-foreground">lab average: {data.averageScore}/100</p>
      </div>

      <div className="border-t border-l border-border grid grid-cols-1 md:grid-cols-2">
        {data.scenarios.map((s) => (
          <Link
            key={s.slug}
            to={`/labs/${s.slug}`}
            data-testid={`lab-${s.slug}`}
            className="border-r border-b border-border p-6 hover:bg-accent/40 transition-colors duration-150"
          >
            <div className="flex items-center justify-between">
              <span className={`overline ${SEVERITY_COLOR[s.severity]}`}>{s.severity} · {s.difficulty}</span>
              <span className="font-mono text-xs text-muted-foreground">{s.category}</span>
            </div>
            <h2 className="mt-4 font-display text-lg font-bold tracking-tight">{s.title}</h2>
            <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{s.symptom}</p>
            <div className="mt-4"><ScoreBar value={s.bestScore || 0} /></div>
            <p className="mt-2 font-mono text-xs text-muted-foreground">
              {s.attempted ? `best score ${s.bestScore}/100` : "not attempted"}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function LabDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/troubleshooting/scenarios/${slug}`);
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 402) setLocked(true);
      setError(errMsg(e, "Unable to load scenario"));
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const payload = { answers: data.scenario.questions.map((_, i) => answers[i] || "") };
      const { data: res } = await api.post(`/troubleshooting/scenarios/${slug}/submit`, payload);
      setResult(res);
      load();
    } catch (e) {
      setError(errMsg(e, "Unable to submit"));
    } finally {
      setBusy(false);
    }
  };

  if (locked)
    return (
      <div className="border border-border p-10 text-center" data-testid="labs-locked">
        <Lock className="h-5 w-5 mx-auto text-primary" />
        <p className="mt-4 font-display text-lg font-bold">Troubleshooting labs are a Pro feature</p>
        <p className="mt-2 text-sm text-muted-foreground">{error}</p>
        <Link to="/pricing" className="inline-block mt-6"><Button className="rounded-none">View plans</Button></Link>
      </div>
    );
  if (!data) return <div className="grid place-items-center py-24" data-testid="lab-loading"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  const s = data.scenario;
  const allAnswered = s.questions.every((_, i) => (answers[i] || "").trim().length >= 15);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 fade-up" data-testid="lab-detail-page">
      <div className="lg:col-span-2 space-y-8">
        <div>
          <Link to="/labs" className="inline-flex items-center gap-2 font-mono text-xs text-muted-foreground hover:text-primary transition-colors duration-150" data-testid="back-to-labs">
            <ArrowLeft className="h-3 w-3" /> all labs
          </Link>
          <div className="mt-4 flex items-center gap-3">
            <span className={`overline ${SEVERITY_COLOR[s.severity]}`}>{s.severity}</span>
            <span className="font-mono text-xs text-muted-foreground">{s.category} · {s.difficulty}</span>
          </div>
          <h1 className="mt-3 text-3xl font-display font-black tracking-tighter leading-none" data-testid="lab-title">{s.title}</h1>
        </div>

        <div className="border-l-2 border-destructive pl-5 py-1">
          <p className="overline text-destructive flex items-center gap-2"><AlertTriangle className="h-3.5 w-3.5" /> symptom</p>
          <p className="mt-2 text-sm text-muted-foreground">{s.symptom}</p>
        </div>

        <div className="space-y-4" data-testid="lab-evidence">
          <p className="overline text-muted-foreground">evidence</p>
          {s.evidence.map((e) => (
            <CodeBlock key={e.label} label={e.label} code={e.content} testId={`evidence-${e.label.replace(/\s+/g, "-")}`} />
          ))}
        </div>

        <div className="space-y-6">
          <p className="overline text-muted-foreground">your reasoning</p>
          {s.questions.map((q, i) => (
            <div key={i}>
              <p className="text-sm font-medium">{i + 1}. {q}</p>
              <Textarea
                data-testid={`lab-answer-${i}`}
                rows={4}
                className="mt-2 rounded-none text-sm"
                placeholder="Explain your reasoning…"
                value={answers[i] || ""}
                onChange={(e) => setAnswers({ ...answers, [i]: e.target.value })}
              />
            </div>
          ))}
          {error && <p data-testid="lab-error" className="text-sm text-destructive">{error}</p>}
          <Button className="rounded-none gap-2" onClick={submit} disabled={busy || !allAnswered} data-testid="lab-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit for grading
          </Button>
          {!allAnswered && <p className="font-mono text-xs text-muted-foreground">Answer all {s.questions.length} questions (15+ chars each) to submit.</p>}
        </div>
      </div>

      <aside className="space-y-6">
        {result ? (
          <div className="border border-border" data-testid="lab-result">
            <div className="px-5 py-4 border-b border-border flex items-baseline justify-between">
              <p className="font-display font-bold tracking-tight">Grade</p>
              <p className="font-mono text-lg" data-testid="lab-score">{result.score}/100</p>
            </div>
            <div className="p-5 space-y-4">
              <ScoreBar value={result.score} />
              <p className="font-mono text-xs text-muted-foreground">graded by {result.gradedBy}</p>
              <div className="border border-border bg-accent/40 p-4 text-xs whitespace-pre-wrap font-mono leading-relaxed" data-testid="lab-feedback">
                {result.feedback}
              </div>
              <div>
                <p className="overline text-muted-foreground">root cause</p>
                <p className="mt-2 text-sm text-muted-foreground">{result.rootCause}</p>
              </div>
              <div>
                <p className="overline text-muted-foreground">resolution</p>
                <p className="mt-2 text-sm text-muted-foreground">{result.resolution}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="border border-dashed border-border p-6">
            <p className="overline text-muted-foreground">how you are graded</p>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2"><span className="text-primary font-mono">›</span>Correct diagnosis from the evidence</li>
              <li className="flex gap-2"><span className="text-primary font-mono">›</span>Safe mitigation, in the right order</li>
              <li className="flex gap-2"><span className="text-primary font-mono">›</span>Prevention that would actually hold</li>
            </ul>
          </div>
        )}

        {data.submissions?.length > 0 && (
          <div className="border border-border p-5" data-testid="lab-attempts">
            <p className="overline text-muted-foreground">previous attempts</p>
            <ul className="mt-3 space-y-1 font-mono text-xs">
              {data.submissions.map((s2, i) => (
                <li key={i} className="text-muted-foreground">attempt {data.submissions.length - i}: {s2.score}/100</li>
              ))}
            </ul>
          </div>
        )}
      </aside>
    </div>
  );
}
